import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TournamentsRepository } from '../services/repository.js';
import { useAuthSession } from '../hooks/useAuthSession.js';
import TournamentTypeFlag from './TournamentTypeFlag.jsx';
import { showSnackbar } from './Snackbar.jsx';
import { useConfirmation } from '../contexts/ConfirmationContext.jsx';
import { getTopPlayers, getPlayersByRank } from '../utils/tournamentRanking.js';
import './TournamentList.css';

const PAGE_SIZE = 20;

const TournamentList = ({ onDeleteClick, openMenuId, setOpenMenuId }) => {
    const [tournaments, setTournaments] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { isAuthenticated, user } = useAuthSession();

    useEffect(() => {
        loadTournaments();
        // Scroll to top when component mounts with instant behavior
        window.scrollTo({ top: 0, behavior: 'instant' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }, [page]);

    // Scroll to top when authentication state changes
    useEffect(() => {
        if (isAuthenticated) {
            // Scroll to top after successful login
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'instant' });
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
            }, 100);
        }
    }, [isAuthenticated]);





    const loadTournaments = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await TournamentsRepository.fetchTournaments({ page, perPage: PAGE_SIZE });
            if (data.totalPages > 0 && page > data.totalPages) {
                setPage(data.totalPages);
                return;
            }
            setTournaments(data.tournaments);
            setTotalPages(Math.max(1, data.totalPages || 1));
        } catch (err) {
            console.error('Error loading tournaments:', err);
            setError(err.message);
        } finally {
            setLoading(false);
            // Ensure scroll to top after content is loaded
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'instant' });
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
            }, 50);
        }
    };

    const calculateTournamentStatus = (tournament) => {
        // Check if the tournament has a finish date (set by the backend)
        return tournament.finishedAt ? 'finished' : 'running';
    };

    const calculatePlayerStats = (tournament) => {
        const status = calculateTournamentStatus(tournament);
        
        if (status === 'finished') {
            // For finished tournaments, show all players with rank 3 or better (handles ties for 3rd place)
            return getPlayersByRank(tournament, 3);
        } else {
            // For tournaments in progress, show only the top 3 players (first, second, third)
            return getTopPlayers(tournament, 3);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Nieznana data';
        const date = new Date(dateString * 1000); // Convert from seconds to milliseconds
        return date.toLocaleDateString('pl-PL', {
            year: 'numeric',
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleMenuToggle = (tournamentId, event) => {
        event.stopPropagation();
        setOpenMenuId(openMenuId === tournamentId ? null : tournamentId);
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            setOpenMenuId(null);
        };
        
        if (openMenuId) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [openMenuId]);

    return (
        <div className="tournament-list-container">
            <div className="tournament-list-header">
                <div className="header-left">
                    <h1>Turnieje</h1>
                </div>
                {isAuthenticated && (
                    <div className="header-right">
                        <button 
                            className="new-tournament-btn"
                            onClick={() => {
                                if (window.openTournamentCreationMenu) {
                                    window.openTournamentCreationMenu();
                                }
                            }}
                        >
                            New
                        </button>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="loading-message">Ładowanie turniejów...</div>
            ) : error ? (
                <div className="error-message">
                    Błąd ładowania turniejów: {error}
                    <button onClick={loadTournaments} className="retry-button">
                        Spróbuj ponownie
                    </button>
                </div>
            ) : tournaments.length === 0 ? (
                <div className="no-tournaments">
                    <p>Nie znaleziono turniejów.</p>
                    {isAuthenticated && (
                        <button 
                            onClick={() => window.openTournamentCreationMenu()} 
                            className="create-first-tournament-btn"
                        >
                            Utwórz swój pierwszy turniej
                        </button>
                    )}
                </div>
            ) : (
                <>
                <div className="tournaments-grid">
                    {tournaments.map(tournament => {
                        const status = calculateTournamentStatus(tournament);
                        const topPlayers = calculatePlayerStats(tournament);
                        

                        
                        return (
                            <div key={tournament.id} className="tournament-card">
                                <div className="tournament-card-header">
                                    <div className="tournament-name">
                                        <Link to={`/tournament/${tournament.id}`}>
                                            <TournamentTypeFlag tournamentType={tournament.type} />
                                            {tournament.name}
                                        </Link>
                                    </div>
                                    <div className="tournament-header-right">
                                        <div className={`tournament-status ${status}`}>
                                            {status === 'finished' ? '✅ Zakończony' : '🎾 W trakcie'}
                                        </div>
                                        {isAuthenticated && (
                                            <div className="tournament-menu">
                                                <button 
                                                    className="menu-toggle-btn"
                                                    onClick={(e) => handleMenuToggle(tournament.id, e)}
                                                >
                                                    ⋯
                                                </button>
                                                {openMenuId === tournament.id && (
                                                    <div className="menu-dropdown">
                                                                                                                    <button 
                                                                className="menu-item delete-item"
                                                                onClick={(e) => onDeleteClick(tournament.id, e)}
                                                            >
                                                                Usuń
                                                            </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="tournament-info">
                                    <div className="tournament-date">
                                        📅 {formatDate(tournament.createdAt)}
                                    </div>
                                    <div className="tournament-details">
                                        🎯 Cel: {tournament.numberOfPoints} punktów
                                    </div>
                                </div>

                                <div className="top-players">
                                    <h4>🏆 Top Gracze</h4>
                                    {topPlayers.length > 0 ? (
                                        <div className="players-list">
                                            {topPlayers.map((player) => (
                                                <div key={player.id} className={`player-rank rank-${Math.min(player.rank, 3)}`}>
                                                    <span className="rank-number">
                                                        {player.rank}
                                                        {topPlayers.filter(p => p.rank === player.rank).length > 1 && 
                                                         <span className="tie-indicator" title="Remis">=</span>
                                                        }
                                                    </span>
                                                    <span className="player-name">{player.name}</span>
                                                    <span className="player-points">{player.points} pkt</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="no-results">Brak wyników</div>
                                    )}
                                </div>

                                <div className="tournament-actions">
                                    <Link 
                                        to={`/tournament/${tournament.id}`} 
                                        className="view-details-btn"
                                    >
                                        Zobacz szczegóły
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {totalPages > 1 && (
                    <nav className="tournament-pagination" aria-label="Paginacja turniejów">
                        <button
                            type="button"
                            className="pagination-btn"
                            disabled={page <= 1 || loading}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            Poprzednia
                        </button>
                        <span className="pagination-status">
                            Strona {page} z {totalPages}
                        </span>
                        <button
                            type="button"
                            className="pagination-btn"
                            disabled={page >= totalPages || loading}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                            Następna
                        </button>
                    </nav>
                )}
                </>
            )}
        </div>
    );
};

// Wrapper component that uses global confirmation context
const TournamentListWithPopup = () => {
    const [openMenuId, setOpenMenuId] = useState(null);
    const [listKey, setListKey] = useState(0);
    const { showConfirmation } = useConfirmation();

    const handleDeleteClick = async (tournamentId, event) => {
        event.stopPropagation();
        setOpenMenuId(null);
        
        const confirmed = await showConfirmation({
            title: "Potwierdzenie usunięcia",
            message: "Czy na pewno chcesz usunąć ten turniej?",
            confirmText: "Usuń",
            cancelText: "Anuluj",
            style: "destructive"
        });

        if (confirmed) {
            try {
                await TournamentsRepository.deleteTournament(tournamentId);
                showSnackbar('Turniej został usunięty');
                setListKey((k) => k + 1);
            } catch (error) {
                console.error('Error deleting tournament:', error);
                showSnackbar('Błąd podczas usuwania turnieju');
            }
        }
    };

    return (
        <TournamentList
            key={listKey}
            onDeleteClick={handleDeleteClick}
            openMenuId={openMenuId}
            setOpenMenuId={setOpenMenuId}
        />
    );
};

export default TournamentListWithPopup;